import React, { useState, useEffect } from 'react';
import { useCart } from 'react-use-cart';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../Contexts/NotificationContext';
import ParticlesBG from '../Shared/ParticlesBG';
import '../Assets/Stylesheets/Pages/Checkout.scss';

// Mapping function to convert frontend item names to database IDs
// For Paid Ads, returns array of [budget_id, media_planning_fee_id]
const mapItemNameToDatabaseIds = (itemName, platform) => {
  const lowerName = itemName.toLowerCase();
  
  // Paid Ads - Facebook combinations (Budget + Media Planning Fee)
  if (lowerName.includes('facebook') && lowerName.includes('introductory')) {
    return [1, 2]; // Facebook Introductory Budget + Media Planning Fee
  }
  if (lowerName.includes('facebook') && lowerName.includes('intermediate')) {
    return [3, 4]; // Facebook Intermediate Budget + Media Planning Fee
  }
  if (lowerName.includes('facebook') && lowerName.includes('advanced')) {
    return [5, 6]; // Facebook Advanced Budget + Media Planning Fee
  }
  if (lowerName.includes('facebook') && lowerName.includes('custom')) {
    return [7, 8]; // Facebook Custom Budget + Media Planning Fee
  }
  
  // Paid Ads - Google combinations (Budget + Media Planning Fee)
  if (lowerName.includes('google') && lowerName.includes('introductory')) {
    return [9, 10]; // Google Introductory Budget + Media Planning Fee
  }
  if (lowerName.includes('google') && lowerName.includes('intermediate')) {
    return [11, 12]; // Google Intermediate Budget + Media Planning Fee
  }
  if (lowerName.includes('google') && lowerName.includes('advanced')) {
    return [13, 14]; // Google Advanced Budget + Media Planning Fee
  }
  if (lowerName.includes('google') && lowerName.includes('custom')) {
    return [15, 16]; // Google Custom Budget + Media Planning Fee
  }
  
  // Single items (non-paid ads)
  const singleItemMappings = {
    'social media growth - facebook - introductory': [500],
    'website - wix': [1000],
    'corporate video': [750],
    'press release - forbes': [2000],
  };
  
  // Try exact match for single items
  for (const [key, ids] of Object.entries(singleItemMappings)) {
    if (lowerName.includes(key.replace(/\s+/g, ' '))) {
      return ids;
    }
  }
  
  // Try partial matching for single items
  if (lowerName.includes('social media')) return [500];
  if (lowerName.includes('wix')) return [1000];
  if (lowerName.includes('video')) return [750];
  if (lowerName.includes('forbes')) return [2000];
  
  // Default fallback - return null to trigger error
  return null;
};

export default function Checkout() {
  const { items, cartTotal, emptyCart, updateItemQuantity, removeItem } = useCart();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Canada',
  });

  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [authoritativeCart, setAuthoritativeCart] = useState(null);

  useEffect(() => {
    // Redirect if cart is empty (but not while we are handing off to the
    // hosted payment page, which empties the cart just before navigating away).
    if (items.length === 0 && !redirecting) {
      navigate('/');
    }
  }, [items, navigate, redirecting]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate customer info
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'Province is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Postal code is required';

    // Payment details are never collected here; they are entered on Zoho's
    // PCI-compliant hosted page after this step.

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setProcessing(true);
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const cartValidationPayload = {
        role: 'buyer',
        currency: 'CAD',
        location: {
          country: formData.country === 'Canada' ? 'CA' : 'US',
          region: formData.state || 'ON',
        },
        items: items.map(item => ({
          service_id: mapCartItemToMarketplaceService(item),
          options: mapCartItemToMarketplaceOptions(item),
          quantity: item.quantity || 1,
          unit_price: (item.price || 0) / (item.quantity || 1),
          confidence: 1,
          price_source: 'catalog',
        })),
      };

      const cartValidationResponse = await fetch(`${API_BASE}/api/marketplace/cart/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartValidationPayload),
      });
      const cartValidation = await cartValidationResponse.json();
      if (!cartValidationResponse.ok || !cartValidation.valid) {
        throw new Error((cartValidation.errors || []).join(', ') || 'Cart validation failed');
      }
      setAuthoritativeCart(cartValidation);

      const sessionResponse = await fetch(`${API_BASE}/api/marketplace/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_proof_hash: cartValidation.proof_hash,
          totals: cartValidation.totals,
          metadata: { source: 'checkout_page' },
        }),
      });
      const checkoutSession = await sessionResponse.json();
      if (!sessionResponse.ok || !checkoutSession.success) {
        throw new Error(checkoutSession.error || 'Checkout session creation failed');
      }

      // Map cart items to Supabase item IDs for Zoho addons. Unmapped items
      // must NOT silently fall back to a real billable item (previously
      // everything unknown was billed as item 2000 / Forbes). Abort instead so
      // the customer is never charged for the wrong product.
      const mapToItemId = (item) => {
        const key = (item.id && typeof item.id === 'string') ? item.id : '';
        if (key.includes('press-release-forbes')) return 2000;
        if (key.includes('corporate-video')) return 750;
        if (key.includes('website')) return 1000;
        if (key.includes('social-media')) return 500;
        return null;
      };

      const addons = [];
      const unmapped = [];
      items.forEach((item) => {
        const mappedId = mapToItemId(item);
        if (mappedId === null) {
          unmapped.push(item.name || item.id || 'item');
        } else {
          addons.push({ item_id: mappedId, quantity: item.quantity || 1 });
        }
      });

      if (unmapped.length > 0) {
        throw new Error(
          `These items cannot be checked out yet: ${unmapped.join(', ')}. ` +
          `Please remove them or contact support.`
        );
      }

      // Create Zoho subscription with Free plan + addons
      const subscriptionData = {
        plan_id: 1, // Free plan
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        addons: addons,
        currency: cartValidation.totals.currency || 'CAD',
        marketplace_cart_proof_hash: cartValidation.proof_hash,
        marketplace_checkout_session_id: checkoutSession.session.session_id,
        authoritative_total: cartValidation.totals.total,
        billing_address: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zipCode,
          country: formData.country || 'Canada'
        }
      };

      console.log('Creating Zoho subscription:', subscriptionData);

      // Use environment variable for API URL, fallback to relative path for production
      const response = await fetch(`${API_BASE}/api/subscription/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData),
      });

      const result = await response.json();
      console.log('Zoho response:', result);

      // Check if we got a hosted page URL (success can be true, or check for URL directly)
      if (result.hosted_page_url) {
        // Redirect to Zoho's PCI-compliant hosted payment page.
        setRedirecting(true);
        showSuccess('Redirecting to secure payment page...');
        emptyCart();
        setTimeout(() => {
          window.location.href = result.hosted_page_url;
        }, 500);
      } else {
        // Log full error for debugging
        console.error('Failed to create payment page:', result);
        throw new Error(result.error?.message || result.message || 'Failed to create payment page');
      }
      
    } catch (error) {
      console.error('Error creating order:', error);
      setProcessing(false);
      showError(`Error creating order: ${error.message}`);
    }
  };

  const formatCurrency = (amount, currency = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const mapCartItemToMarketplaceService = (item) => {
    const haystack = `${item.id || ''} ${item.name || ''}`.toLowerCase();
    if (haystack.includes('paid') || haystack.includes('ads') || haystack.includes('google') || haystack.includes('facebook')) {
      return 'paid_ads';
    }
    if (haystack.includes('website') || haystack.includes('webflow') || haystack.includes('wix') || haystack.includes('wordpress')) {
      return 'website';
    }
    return 'social_media_management';
  };

  const mapCartItemToMarketplaceOptions = (item) => {
    const haystack = `${item.id || ''} ${item.name || ''} ${item.platform || ''}`.toLowerCase();
    if (haystack.includes('linkedin')) return ['linkedin'];
    if (haystack.includes('tiktok')) return ['tiktok'];
    if (haystack.includes('google')) return ['google'];
    if (haystack.includes('facebook') || haystack.includes('meta')) return ['facebook'];
    if (haystack.includes('webflow')) return ['webflow'];
    if (haystack.includes('wix')) return ['wix'];
    if (haystack.includes('wordpress')) return ['wordpress'];
    return ['instagram'];
  };

  return (
    <div className="checkout-page">
      <ParticlesBG />
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        
        <div className="checkout-content">
          {/* Order Summary */}
          <div className="order-summary-section">
            <h2>Order Summary</h2>
            <div className="order-items">
              {items.map((item) => (
                <div className="order-item" key={item.id}>
                  <div className="item-info">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="item-image" />
                    )}
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      {item.platform && <p className="item-platform">{item.platform}</p>}
                      {item.description && (
                        <p className="item-description">{item.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="item-actions">
                    <div className="item-quantity-controls">
                      <button 
                        className="quantity-btn"
                        onClick={() => {
                          const currentQty = item.quantity || 1;
                          if (currentQty > 1) {
                            updateItemQuantity(item.id, currentQty - 1);
                          }
                        }}
                        title="Decrease quantity"
                        disabled={(!item.quantity || item.quantity <= 1)}
                      >
                        -
                      </button>
                      <span className="quantity-value">{item.quantity || 1}</span>
                      <button 
                        className="quantity-btn"
                        onClick={() => {
                          const currentQty = item.quantity || 1;
                          updateItemQuantity(item.id, currentQty + 1);
                        }}
                        title="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <div className="item-price">
                      {(item.quantity || 1) > 1 && (
                        <div className="price-breakdown">
                          <span className="unit-price">{formatCurrency(item.price / (item.quantity || 1))} each</span>
                          <span className="total-price">{formatCurrency(item.price)}</span>
                        </div>
                      )}
                      {(!item.quantity || item.quantity === 1) && (
                        <span className="single-price">{formatCurrency(item.price)}</span>
                      )}
                    </div>
                    <button 
                      className="item-remove-btn"
                      onClick={() => removeItem(item.id)}
                      title="Remove item"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="order-total">
              <span>Total:</span>
              <span className="total-amount">
                {authoritativeCart
                  ? formatCurrency(authoritativeCart.totals.total, authoritativeCart.totals.currency)
                  : formatCurrency(cartTotal)}
              </span>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="checkout-form-section">
            <form onSubmit={handleSubmit}>
              {/* Customer Information */}
              <div className="form-section">
                <h2>Customer Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company (Optional)</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={errors.address ? 'error' : ''}
                  />
                  {errors.address && <span className="error-message">{errors.address}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={errors.city ? 'error' : ''}
                    />
                    {errors.city && <span className="error-message">{errors.city}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">Province *</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={errors.state ? 'error' : ''}
                    />
                    {errors.state && <span className="error-message">{errors.state}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="zipCode">Postal Code *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className={errors.zipCode ? 'error' : ''}
                    />
                    {errors.zipCode && <span className="error-message">{errors.zipCode}</span>}
                  </div>
                </div>
              </div>

              {/* Payment handled off-site */}
              <div className="form-section">
                <h2>Payment</h2>
                <div className="payment-note">
                  🔒 Payment is processed securely on our provider's PCI-compliant
                  hosted page. Card details are never entered on or stored by this
                  site. You will be redirected to complete your purchase.
                </div>
              </div>

              {/* Submit Button */}
              <div className="checkout-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => navigate(-1)}
                  disabled={processing}
                >
                  ← Back
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : (
                    `Pay ${authoritativeCart
                      ? formatCurrency(authoritativeCart.totals.total, authoritativeCart.totals.currency)
                      : formatCurrency(cartTotal)}`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

